import {FC, useState, useEffect} from 'react'
import {Modal} from 'react-bootstrap'

type TableType = {
  id: number
  capacity: number
  description: string
}

type Table = {
  customCapacity?: number
  tableType?: {
    capacity: number
  }
}

type Props = {
  show: boolean
  onHide: () => void
  onSave: (tables: any[]) => void
  tableTypes: TableType[]
  existingTableNumbers: string[]
  existingTables: Table[]
  maxTables: number
  maxSeats: number
}

export const BulkTableCreationModal: FC<Props> = ({
  show,
  onHide,
  onSave,
  tableTypes,
  existingTableNumbers,
  existingTables,
  maxTables,
  maxSeats,
}) => {
  const [prefix, setPrefix] = useState('M')
  const [startNumber, setStartNumber] = useState(1)
  const [quantity, setQuantity] = useState(10)
  const [tableTypeId, setTableTypeId] = useState<number>(-1)
  const [customCapacity, setCustomCapacity] = useState<number>(4)
  const [errors, setErrors] = useState<string[]>([])
  const [previewTables, setPreviewTables] = useState<string[]>([])

  // Calcular capacidade atual e limites disponíveis
  const currentTablesCount = existingTables.length
  const currentSeatsUsed = existingTables.reduce(
    (sum, t) => sum + (t.customCapacity || t.tableType?.capacity || 0),
    0
  )

  const availableTables = maxTables > 0 ? maxTables - currentTablesCount : Infinity
  const availableSeats = maxSeats > 0 ? maxSeats - currentSeatsUsed : Infinity

  const hasTableLimit = maxTables > 0
  const hasSeatsLimit = maxSeats > 0

  useEffect(() => {
    if (show) {
      generatePreview()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefix, startNumber, quantity, show, tableTypeId, customCapacity])

  const generatePreview = () => {
    const preview: string[] = []
    const conflicts: string[] = []

    for (let i = 0; i < quantity; i++) {
      const num = startNumber + i
      const paddedNum = num.toString().padStart(2, '0')
      const tableName = `${prefix}${paddedNum}`

      preview.push(tableName)

      if (existingTableNumbers.includes(tableName)) {
        conflicts.push(tableName)
      }
    }

    setPreviewTables(preview)

    if (conflicts.length > 0) {
      setErrors([`As seguintes mesas já existem: ${conflicts.join(', ')}`])
    } else {
      setErrors([])
    }
  }

  const handleSave = () => {
    const newErrors: string[] = []

    if (!prefix.trim()) {
      newErrors.push('Prefixo é obrigatório')
    }

    if (startNumber < 1) {
      newErrors.push('Número inicial deve ser maior que 0')
    }

    if (quantity < 1 || quantity > 100) {
      newErrors.push('Quantidade deve estar entre 1 e 100')
    }

    if (tableTypeId === -1 && (!customCapacity || customCapacity < 1)) {
      newErrors.push('Capacidade customizada deve ser maior que 0')
    }

    if (tableTypeId !== -1 && !tableTypes.find((t) => t.id === tableTypeId)) {
      newErrors.push('Tipo de mesa inválido')
    }

    // Validar limite de mesas
    if (hasTableLimit && quantity > availableTables) {
      newErrors.push(
        `Limite de mesas excedido. Você pode criar no máximo ${availableTables} mesa(s). (Atual: ${currentTablesCount}/${maxTables})`
      )
    }

    // Validar limite de capacidade
    if (hasSeatsLimit) {
      const capacityPerTable =
        tableTypeId === -1
          ? customCapacity
          : tableTypes.find((t) => t.id === tableTypeId)?.capacity || 0
      const totalNewCapacity = quantity * capacityPerTable

      if (totalNewCapacity > availableSeats) {
        newErrors.push(
          `Capacidade excedida. Você pode adicionar no máximo ${availableSeats} lugar(es). (Atual: ${currentSeatsUsed}/${maxSeats}). As ${quantity} mesas teriam ${totalNewCapacity} lugares.`
        )
      }
    }

    if (newErrors.length > 0) {
      setErrors(newErrors)
      return
    }

    // Verificar conflitos novamente
    const conflicts = previewTables.filter((name) => existingTableNumbers.includes(name))
    if (conflicts.length > 0) {
      setErrors([`As seguintes mesas já existem: ${conflicts.join(', ')}`])
      return
    }

    // Gerar array de mesas
    const tables = previewTables.map((tableName) => ({
      tableNumber: tableName,
      tableTypeId: tableTypeId,
      customCapacity: tableTypeId === -1 ? customCapacity : null,
    }))

    onSave(tables)
    handleClose()
  }

  const handleClose = () => {
    setPrefix('M')
    setStartNumber(1)
    setQuantity(10)
    setTableTypeId(-1)
    setCustomCapacity(4)
    setErrors([])
    setPreviewTables([])
    onHide()
  }

  return (
    <Modal show={show} onHide={handleClose} size='lg' centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className='bi bi-plus-circle me-2'></i>
          Criar Mesas em Lote
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Alerta com limites disponíveis */}
        <div className='alert alert-info d-flex align-items-center mb-4'>
          <i className='bi bi-info-circle fs-2 me-3'></i>
          <div className='flex-grow-1'>
            <strong>Limites do Ambiente:</strong>
            <div className='row mt-2'>
              <div className='col-6'>
                <span className='badge badge-light-primary fs-6'>
                  Mesas: {currentTablesCount} / {hasTableLimit ? maxTables : '∞'}
                </span>
                {hasTableLimit && (
                  <small className='d-block text-muted mt-1'>
                    Disponível: {availableTables} mesa(s)
                  </small>
                )}
              </div>
              <div className='col-6'>
                <span className='badge badge-light-success fs-6'>
                  Lugares: {currentSeatsUsed} / {hasSeatsLimit ? maxSeats : '∞'}
                </span>
                {hasSeatsLimit && (
                  <small className='d-block text-muted mt-1'>
                    Disponível: {availableSeats} lugar(es)
                  </small>
                )}
              </div>
            </div>
          </div>
        </div>

        {errors.length > 0 && (
          <div className='alert alert-danger'>
            <ul className='mb-0'>
              {errors.map((err, idx) => (
                <li key={idx}>{err}</li>
              ))}
            </ul>
          </div>
        )}

        <div className='row mb-4'>
          <div className='col-md-4'>
            <label className='form-label required'>Prefixo</label>
            <input
              type='text'
              className='form-control'
              value={prefix}
              onChange={(e) => setPrefix(e.target.value.toUpperCase())}
              placeholder='M'
              maxLength={5}
            />
            <small className='text-muted'>Ex: M, MESA, T</small>
          </div>

          <div className='col-md-4'>
            <label className='form-label required'>Número Inicial</label>
            <input
              type='number'
              className='form-control'
              value={startNumber}
              onChange={(e) => setStartNumber(Number(e.target.value))}
              min={1}
            />
            <small className='text-muted'>Ex: 1, 10, 100</small>
          </div>

          <div className='col-md-4'>
            <label className='form-label required'>Quantidade</label>
            <input
              type='number'
              className='form-control'
              value={quantity}
              onChange={(e) => setQuantity(Number(e.target.value))}
              min={1}
              max={100}
            />
            <small className='text-muted'>Máximo: 100</small>
          </div>
        </div>

        <div className='mb-4'>
          <label className='form-label required'>Tipo de Mesa</label>
          <select
            className='form-select'
            value={tableTypeId}
            onChange={(e) => setTableTypeId(Number(e.target.value))}
          >
            <option value={-1}>Personalizado (Capacidade Customizada)</option>
            {tableTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.description} - {type.capacity} pessoa(s)
              </option>
            ))}
          </select>
        </div>

        {tableTypeId === -1 && (
          <div className='mb-4'>
            <label className='form-label required'>Capacidade (pessoas)</label>
            <input
              type='number'
              className='form-control'
              value={customCapacity}
              onChange={(e) => setCustomCapacity(Number(e.target.value))}
              min={1}
              max={50}
            />
          </div>
        )}

        <div className='mb-4'>
          <label className='form-label'>
            <i className='bi bi-eye me-2'></i>
            Pré-visualização ({previewTables.length} mesas)
          </label>
          <div
            className='border rounded p-3'
            style={{maxHeight: '200px', overflowY: 'auto', backgroundColor: '#f8f9fa'}}
          >
            <div className='d-flex flex-wrap gap-2'>
              {previewTables.map((name, idx) => (
                <span
                  key={idx}
                  className={`badge ${
                    existingTableNumbers.includes(name) ? 'badge-danger' : 'badge-primary'
                  } fs-6`}
                >
                  {name}
                  {existingTableNumbers.includes(name) && (
                    <i className='bi bi-exclamation-triangle ms-1'></i>
                  )}
                </span>
              ))}
            </div>
          </div>
          <small className='text-muted'>Mesas em vermelho já existem e serão ignoradas.</small>
        </div>

        <div className='alert alert-info'>
          <i className='bi bi-info-circle me-2'></i>
          <strong>Resumo:</strong> Serão criadas {previewTables.length} mesas do tipo{' '}
          {tableTypeId === -1
            ? `Personalizado (${customCapacity} pessoa(s))`
            : tableTypes.find((t) => t.id === tableTypeId)?.description}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button type='button' className='btn btn-secondary' onClick={handleClose}>
          Cancelar
        </button>
        <button
          type='button'
          className='btn btn-primary'
          onClick={handleSave}
          disabled={errors.length > 0}
        >
          <i className='bi bi-check-circle me-2'></i>
          Criar {previewTables.length} Mesa(s)
        </button>
      </Modal.Footer>
    </Modal>
  )
}
